/**
 * Callback Triggers for React Command Flow
 *
 * Helper functions for triggering callbacks from within commands.
 */

/**
 * Creates callback trigger functions that can be injected into commands
 * @param {Object} callbackRegistry - The callback registry
 * @param {Object} context - Current command context (commandName, sessionData, etc.)
 * @returns {Object} - Trigger functions for use in commands
 */
export function createCallbackTriggers(callbackRegistry, context = {}) {
  return {
    // Simple trigger - fire and forget
    fire: (callbackName, data = {}) => {
      return callbackRegistry.trigger(callbackName, data, context);
    },

    // Trigger with response handling
    call: async (callbackName, data = {}) => {
      try {
        return await callbackRegistry.trigger(callbackName, data, context);
      } catch (error) {
        console.error(`Callback "${callbackName}" failed:`, error);
        return { error: error.message };
      }
    },

    // Trigger multiple callbacks
    fireMultiple: (callbackNames, data = {}) => {
      return callbackRegistry.triggerSequence(callbackNames, data, context);
    },

    // Trigger with conditional logic
    fireIf: (condition, callbackName, data = {}) => {
      if (condition) {
        return callbackRegistry.trigger(callbackName, data, context);
      }
      return Promise.resolve(null);
    },

    // Trigger with data transformation
    fireWith: (callbackName, dataTransformer) => {
      const transformedData =
        typeof dataTransformer === "function"
          ? dataTransformer(context)
          : dataTransformer;
      return callbackRegistry.trigger(callbackName, transformedData, context);
    },
  };
}
